/**
 * Вид, с которым карта открывается впервые: рамка первого доступного района.
 *
 * До этого центр был зашит в `MapCanvas` (`[150, 47]`, Дальний Восток). На
 * домене партнёра, у которого есть только Балтика, это означало карту чужого
 * океана: район в списке один, а карта над Курилами.
 *
 * Набор районов приходит эффективным (`brand.regions ∩ org.allowed_regions`)
 * ответом `/species` — тем же, из которого берётся фильтр видов. Первый в нём
 * и есть район входа: порядок задаёт `ref_region.display_order`, то есть
 * админка, а не витрина.
 *
 * ДОЛГОТА ВОСТОЧНАЯ И НЕПРЕРЫВНАЯ, как везде в проекте: Дальний Восток —
 * `118…202`, и центр у него `160`, а не `−20`. Приводить к знаковой шкале
 * здесь нельзя — MapLibre принимает долготу вне `[−180, 180]` и сам сворачивает
 * её правильно, а вот среднее от свёрнутых краёв уехало бы на другую сторону
 * планеты.
 */

import type { RegionItem } from '../api'
import type { MapView } from '../map/mapView'

/** Условный размер полотна карты в пикселях: рельс слева, но порядок такой. */
const CANVAS_W = 1000
const CANVAS_H = 700
/** Ширина мира в пикселях при зуме 0 — константа MapLibre. */
const TILE = 512

/** Зум не уводим ни в глобус, ни в бухту: рамка района — подсказка, а не приказ. */
const ZOOM_MIN = 2
const ZOOM_MAX = 8

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

/**
 * Зум, при котором рамка помещается в полотно.
 *
 * Широта считается с поправкой на Меркатора (`/cos φ`): у Балтики (53…66° с.ш.)
 * градус широты на экране почти вдвое длиннее градуса на экваторе, и без
 * поправки район вылезал бы за верхний и нижний край.
 */
export function zoomForBox(box: RegionItem['bbox']): number {
  const lonSpan = Math.max(box.lon_max - box.lon_min, 1e-6)
  const latSpan = Math.max(box.lat_max - box.lat_min, 1e-6)
  const latC = ((box.lat_min + box.lat_max) / 2) * (Math.PI / 180)
  // cos φ у полюса вырождается в ноль — прижимаем, иначе зум уходит в −∞.
  const stretched = latSpan / Math.max(Math.cos(latC), 0.05)

  const byLon = Math.log2((CANVAS_W / TILE) * (360 / lonSpan))
  const byLat = Math.log2((CANVAS_H / TILE) * (360 / stretched))
  return clamp(Math.min(byLon, byLat), ZOOM_MIN, ZOOM_MAX)
}

/** Вид входа по эффективному набору районов. `null` — набора ещё нет. */
export function homeView(regions: RegionItem[] | undefined): MapView | null {
  const first = regions?.[0]
  if (!first) return null
  const box = first.bbox
  return {
    lng: (box.lon_min + box.lon_max) / 2,
    lat: (box.lat_min + box.lat_max) / 2,
    zoom: Math.round(zoomForBox(box) * 100) / 100,
  }
}

/**
 * Вид, с которым строить карту. `null` означает «ещё не решено, не строить».
 *
 * Порядок: ссылка и память раздела сильнее рамки района — человек пришёл по
 * своей ссылке или вернулся в раздел, и подменять его вид «домашним» нельзя.
 *
 * ЖДАТЬ, А НЕ ПРЫГАТЬ. Карту можно было бы построить сразу на зашитом центре и
 * перелететь, когда приедет набор районов, — но на домене партнёра это ровно
 * тот кадр чужого океана, ради которого всё и делается. Ожидание стоит одного
 * ответа `/species`, который раздел и так запрашивает, и случается только на
 * первом заходе: дальше вид лежит в памяти раздела.
 */
export function viewToOpen(
  saved: MapView,
  home: MapView | null,
  regionsKnown: boolean,
): MapView | null {
  if (saved.lng != null && saved.lat != null) return saved
  if (!regionsKnown) return null
  return home ?? saved
}
